import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import useIcons from '@/composables/useIcons'
  import UsersApi from '@/services/api/UsersApi'
  import useBreakpoint from '@/composables/useBreakpoint'
  import PanelStepContent from '@/components/get-started-panel/PanelStepContent.vue'
  import useSegment from '@/composables/useSegment'
  import GetStartedPanelLoader from '@/components/loaders/GetStartedPanelLoader.vue'
  import useI18n from '@/composables/useI18n'
  import type { GetStartedStepDetails, OnboardingDetails } from '@/services/interfaces/Common'
  import { UserModule } from '@/store/modules/user'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'GetStartedPanel',
  setup(__props) {

  const { checkmark, arrowRightIndicator } = useIcons()
  const { isMobileDevice } = useBreakpoint()
  const { getStartedStepClicked } = useSegment()
  const { translateString } = useI18n()

  const activeStep = ref(1)
  const onboardingDetails = ref(null as OnboardingDetails | null)
  const completedSteps = ref([] as number[])
  const selectableSteps = ref([] as number[])
  const showPanelBody = ref(true)
  const rerenderKey = ref('')

  const getStartedSteps: GetStartedStepDetails[] = [
    {
      stepNumber: 1,
      text: translateString('homepage.getStartedPanel.chooseTrainings'),
      emptyStateTitle: translateString('homepage.getStartedPanel.selectTrainingsTitle'),
      emptyStateDescription: translateString('homepage.getStartedPanel.selectTrainingsDescription'),
      emptyStateCTA: translateString('homepage.getStartedPanel.andSoItGoes'),
      entity: 'select-trainings',
    },
    {
      stepNumber: 2,
      text: translateString('homepage.getStartedPanel.getTrainingsOverview'),
      emptyStateTitle: translateString('homepage.getStartedPanel.trainingOverviewTitle'),
      emptyStateDescription: translateString('homepage.getStartedPanel.trainingOverviewDescription'),
      emptyStateCTA: translateString('homepage.getStartedPanel.andItsThatEasy'),
      entity: 'trainings-overview',
    },
    {
      stepNumber: 3,
      text: translateString('homepage.getStartedPanel.completeYourFirstQuiz'),
      emptyStateTitle: translateString('homepage.getStartedPanel.completeGuidedQuiz'),
      emptyStateDescription: translateString('homepage.getStartedPanel.completeGuidedQuizDescription'),
      emptyStateCTA: translateString('homepage.getStartedPanel.andItsThatEasy'),
      entity: 'guided-quiz',
    },
    {
      stepNumber: 4,
      text: translateString('homepage.getStartedPanel.answer100Questions'),
      emptyStateTitle: translateString('homepage.getStartedPanel.answeredQuestionsTitle'),
      emptyStateDescription: translateString('homepage.getStartedPanel.answeredQuestionsDescription'),
      emptyStateCTA: '',
      entity: 'questions-answered',
    },
  ]

  const fetchingOnboardingDetails = computed(() => onboardingDetails.value === null)

  const inTrialOrPurchasedCourses = computed(() => onboardingDetails.value?.courses)

  const completedSettingCourseTrial = computed(() => {
    if (!inTrialOrPurchasedCourses.value) return false
    return inTrialOrPurchasedCourses.value.length > 0
  })

  const completedAtLeastOneLesson = computed(() => {
    if (!onboardingDetails.value) return false
    return onboardingDetails.value.totalLessonsCompleted > 0
  })

  const completedAtLeastOneQuiz = computed(() => {
    if (!onboardingDetails.value) return false
    return onboardingDetails.value.guidedQuizzesCompleted > 0
  })

  const totalQuestionsAnswered = computed(() => onboardingDetails.value?.totalQuestionsAnswered ?? 0)

  const totalQuestionsAnsweredPercentage = computed(() => {
    if (!onboardingDetails.value) return 0
    const percentage = Math.round((totalQuestionsAnswered.value / 100) * 100)
    if (percentage > 100) return 100
    return percentage
  })

  const stepIsCompleted = computed(() => (stepNumber: number) => completedSteps.value.includes(stepNumber))

  const stepIsSelectable = computed(() => (stepNumber: number) => selectableSteps.value.includes(stepNumber))

  const completionStatus = computed(() => `[${completedSteps.value.length}/${getStartedSteps.length}]`)

  const allStepsCompleted = computed(() => completedSteps.value.length === getStartedSteps.length)

  const stepEmptyStateTitle = computed(
    () => getStartedSteps.find((step) => step.stepNumber === activeStep.value)?.emptyStateTitle,
  )

  const stepEmptyStateDescription = computed(
    () => getStartedSteps.find((step) => step.stepNumber === activeStep.value)?.emptyStateDescription,
  )

  const stepEmptyStateCTA = computed(
    () => getStartedSteps.find((step) => step.stepNumber === activeStep.value)?.emptyStateCTA,
  )

  const activeStepEntity = computed(() => getStartedSteps.find((step) => step.stepNumber === activeStep.value)?.entity)

  const togglePanelBody = () => {
    if (!allStepsCompleted.value) return
    showPanelBody.value = !showPanelBody.value
  }

  watch(showPanelBody, (value: boolean) => {
    if (value) {
      setTimeout(() => (rerenderKey.value = Math.random().toString(36)), 100)
    }
  })

  const setActiveStep = (stepNumber: number) => {
    if (selectableSteps.value.includes(stepNumber)) {
      activeStep.value = stepNumber
      if (activeStepEntity.value) {
        getStartedStepClicked(activeStep.value, activeStepEntity.value, stepIsCompleted.value(activeStep.value))
      }
    }
  }

  const setCompletedSteps = () => {
    completedSteps.value = []
    if (completedSettingCourseTrial.value) {
      completedSteps.value.push(1)
    }
    if (completedAtLeastOneLesson.value) {
      completedSteps.value.push(2)
    }
    if (completedAtLeastOneQuiz.value) {
      completedSteps.value.push(3)
    }
    if (totalQuestionsAnsweredPercentage.value === 100) {
      completedSteps.value.push(4)
    }
    if (allStepsCompleted.value) showPanelBody.value = false
  }

  const setSelectableSteps = () => {
    // the first step is selectable by default
    selectableSteps.value = [1]

    // a step is selectable if all steps preceding it are completed
    const inCompleteSteps: number[] = []
    for (let i = 1; i < 5; i++) {
      if (stepIsCompleted.value(i)) {
        selectableSteps.value.push(i)
      } else {
        inCompleteSteps.push(i)
      }
    }
    selectableSteps.value.push(inCompleteSteps[0])
    setActivePanelStep()
  }

  const setActivePanelStep = () => {
    if (allStepsCompleted.value) {
      activeStep.value = 1
    } else {
      // basically the first selectable step that is not completed
      const sortedSelectableSteps = selectableSteps.value.sort()
      const inCompleteSelectableSteps = sortedSelectableSteps.filter(function (obj) {
        return completedSteps.value.indexOf(obj) == -1
      })
      activeStep.value = inCompleteSelectableSteps[0]
    }
    if (activeStepEntity.value) {
      getStartedStepClicked(activeStep.value, activeStepEntity.value, stepIsCompleted.value(activeStep.value))
    }
  }

  const getOnboardingDetails = () => {
    UsersApi.getOnboardingDetails()
      .then((details) => {
        onboardingDetails.value = details
      })
      .finally(() => {
        setCompletedSteps()
        setSelectableSteps()
      })
  }

  watch(allStepsCompleted, (value) => {
    if (value) {
      UserModule.setShowGetStartedPanel(false)
    }
  })

  onMounted(() => {
    getOnboardingDetails()
  })

return { __sfc: true,checkmark, arrowRightIndicator, isMobileDevice, getStartedStepClicked, translateString, activeStep, onboardingDetails, completedSteps, selectableSteps, showPanelBody, rerenderKey, getStartedSteps, fetchingOnboardingDetails, inTrialOrPurchasedCourses, completedSettingCourseTrial, completedAtLeastOneLesson, completedAtLeastOneQuiz, totalQuestionsAnswered, totalQuestionsAnsweredPercentage, stepIsCompleted, stepIsSelectable, completionStatus, allStepsCompleted, stepEmptyStateTitle, stepEmptyStateDescription, stepEmptyStateCTA, activeStepEntity, togglePanelBody, setActiveStep, setCompletedSteps, setSelectableSteps, setActivePanelStep, getOnboardingDetails, computed, onMounted, ref, watch, SVGRenderer, useIcons, UsersApi, useBreakpoint, PanelStepContent, useSegment, GetStartedPanelLoader, useI18n, UserModule }
}

})